import {
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_regular,
  font_family_weight_semibold,
  font_lg,
  font_md,
  font_size_sm,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Fills the `Box` the panel renders into. `height: 100%` rather than a fixed
 * height so the panel can grow to whatever the row's tallest cell comes out to
 * — the details row is `align-items: stretch` at the container level, and a
 * fixed height (as the Reporting dashboard's `chartPanel` uses) would leave the
 * chart short whenever it is the taller tile.
 *
 * Card details opts OUT of that stretch on its own cell (`CARD_DETAILS_CELL_CLASS`
 * in `accountOverviewTab.style.ts`), so it no longer inflates to match this
 * panel — this chart now drives the row height whenever it is the taller tile,
 * and `height: 100%` here exists to fill a Box the grid still stretches.
 */
export const panel = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`

/**
 * The same kicker as `cardOverviewCardDetails.style.ts`, so the two panels in
 * the details row carry one heading treatment. Duplicated for the same reason
 * that one is: the shared original lives under `src/page-components/Accounts/`
 * and a feature must not depend on a page.
 *
 * Uppercased via CSS, never in the translation source, so the accessible name
 * stays the plain phrase.
 */
export const title = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
  margin: 0 0 ${spacing(4)};
`

/**
 * Top category on the left, total pushed to the right edge, per the design.
 * `space-between` rather than a fixed gap so the total stays pinned to the right
 * when the top category is absent (nothing attributed) and the row has a single
 * child.
 */
export const statsRow = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing(4)} ${spacing(8)};
  margin-bottom: ${spacing(5)};
`

export const stat = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
`

export const statLabel = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

export const statValue = css`
  ${font_lg};
  color: ${text_colorTextPrimary};
  font-variant-numeric: tabular-nums;
`

/** The headline stat — same scale as `statValue`, but carries the swatch. */
export const topCategoryValue = css`
  ${statValue};
  display: inline-flex;
  align-items: center;
  gap: ${spacing(2)};
`

/**
 * The dot tying the headline to its bar. `flex-shrink: 0` because a long
 * category name must not be allowed to squash it into an ellipse.
 */
export const swatch = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`

/**
 * The chart's frame. `min-height` is the floor for the case where the card
 * details column is short: `VisxStackedBarChart` measures this element with a
 * `ResizeObserver` and falls back to its own 320px default only when the
 * measurement is unavailable, so without a floor the chart would collapse.
 *
 * The `> div` rule mirrors the Reporting dashboard's `chartBody` — the renderer
 * wraps its SVG in a positioning div that has to inherit the full box.
 */
export const chartBody = css`
  display: flex;
  flex: 1;
  min-height: 260px;
  align-items: stretch;

  > div {
    width: 100%;
    height: 100%;
  }
`

export const emptyMessage = css`
  ${font_md};
  display: flex;
  flex: 1;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: ${text_colorTextSecondary};
  text-align: center;
`
